import { useState, useRef, useEffect } from "react";
import { Plus, Send, CheckCircle2, MessageSquare } from "lucide-react";
import { Button, Modal, Form, Input, Select, Spin, Tag, message } from "antd";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { IThread, IMessage } from "../../../types/api/messaging";
import { useThreadList, useThreadMessages, useCreateThread, useSendMessage, useResolveThread } from "../../../hooks/api/messages/hooks";
import { useFieldWorkerList } from "../../../hooks/api/field-workers/hooks";

dayjs.extend(relativeTime);

const STATUS_COLOR: Record<string, string> = { open: "blue", pending: "orange", resolved: "green" };

function NewThreadModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [form] = Form.useForm();
  const [msgApi, ctx] = message.useMessage();
  const { data: workerRes } = useFieldWorkerList({ page: 1, limit: 100 });
  const workers = (workerRes?.data as any[]) ?? [];

  const createThread = useCreateThread({
    onSuccess: () => { msgApi.success("Thread created."); onClose(); form.resetFields(); },
    onError: (e) => msgApi.error(e?.response?.data?.message || "Failed"),
  });

  return (
    <Modal
      open={open}
      onCancel={() => { onClose(); form.resetFields(); }}
      title="New Message Thread"
      width={480}
      footer={[
        <Button key="cancel" onClick={() => { onClose(); form.resetFields(); }}>Cancel</Button>,
        <Button key="send" type="primary" loading={createThread.isPending} onClick={() => form.submit()}>Send</Button>,
      ]}
    >
      {ctx}
      <Form form={form} layout="vertical" size="middle" className="pt-2" onFinish={(vals) => createThread.mutate(vals)}>
        <Form.Item name="assigned_to" label="Send To (Worker)">
          <Select
            showSearch
            placeholder="Select worker…"
            filterOption={(v, o) => (o?.label as string)?.toLowerCase().includes(v.toLowerCase())}
            options={workers.map((w: any) => ({
              value: w.id,
              label: [w.first_name, w.last_name].filter(Boolean).join(" ") || w.email,
            }))}
          />
        </Form.Item>
        <Form.Item name="subject" label="Subject">
          <Input placeholder="e.g. Job site update, PPE reminder" />
        </Form.Item>
        <Form.Item name="body" label="Message" rules={[{ required: true, message: "Message required" }]}>
          <Input.TextArea rows={4} placeholder="Type your message…" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function ChatPanel({ thread, onResolve }: { thread: IThread; onResolve: () => void }) {
  const [body, setBody] = useState("");
  const bottomRef = useRef<HTMLDivElement>(null);
  const [msgApi, ctx] = message.useMessage();

  const { data: res, isLoading } = useThreadMessages(thread.id);
  const messages: IMessage[] = (res?.data as any)?.messages ?? [];

  const send = useSendMessage({
    onSuccess: () => setBody(""),
    onError: (e) => msgApi.error(e?.response?.data?.message || "Failed"),
  });

  const resolve = useResolveThread({ onSuccess: onResolve });

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

  return (
    <div className="flex flex-col h-full">
      {ctx}
      {/* header */}
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
        <div>
          <div className="font-semibold text-slate-800 text-sm">{thread.subject || "No subject"}</div>
          <div className="text-xs text-slate-400">{thread.assigned_to_name ? `To: ${thread.assigned_to_name}` : "General thread"}</div>
        </div>
        {thread.status !== "resolved" && (
          <Button size="small" icon={<CheckCircle2 size={12} />} onClick={() => resolve.mutate(thread.id)} loading={resolve.isPending}>
            Resolve
          </Button>
        )}
      </div>

      {/* messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-slate-50/40">
        {isLoading ? (
          <div className="flex justify-center py-10"><Spin /></div>
        ) : messages.length === 0 ? (
          <div className="text-center text-slate-400 text-sm py-10">No messages yet.</div>
        ) : (
          messages.map((m) => {
            const isOut = m.direction === "outbound";
            return (
              <div key={m.id} className={`flex ${isOut ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm shadow-sm ${isOut ? "text-white rounded-br-sm" : "bg-white text-slate-800 border border-slate-100 rounded-bl-sm"}`} style={isOut ? { background: "var(--color-primary)" } : {}}>
                  {!isOut && m.sender_name && <div className="text-[10px] font-semibold mb-1 text-primary">{m.sender_name}</div>}
                  <div className="whitespace-pre-wrap">{m.body}</div>
                  <div className={`text-[10px] mt-1 ${isOut ? "text-blue-200" : "text-slate-400"}`}>
                    {dayjs(m.created_at).fromNow()}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      {/* compose */}
      {thread.status !== "resolved" && (
        <div className="px-4 py-3 border-t border-slate-100 flex gap-2 bg-white">
          <Input.TextArea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); if (body.trim()) send.mutate({ thread_id: thread.id, body: body.trim() }); } }}
            placeholder="Type a message… (Enter to send)"
            autoSize={{ minRows: 1, maxRows: 4 }}
            className="flex-1"
          />
          <Button
            type="primary"
            icon={<Send size={14} />}
            loading={send.isPending}
            onClick={() => { if (body.trim()) send.mutate({ thread_id: thread.id, body: body.trim() }); }}
                       disabled={!body.trim()}
          />
        </div>
      )}
    </div>
  );
}

const MessagingPage = () => {
  const [newOpen, setNewOpen] = useState(false);
  const [activeThread, setActiveThread] = useState<IThread | null>(null);

  const { data: res, isLoading, refetchWithQuery } = useThreadList({ page: 1, limit: 30 });
  const threads: IThread[] = (res?.data as any) ?? [];

  return (
    <div className="flex h-[calc(100vh-64px)] overflow-hidden">
      {/* sidebar */}
      <div className="w-72 flex-shrink-0 border-r border-slate-100 bg-white flex flex-col">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <span className="font-semibold text-slate-800">Messages</span>
          <Button size="small" type="primary" icon={<Plus size={12} />} onClick={() => setNewOpen(true)}>
            New
          </Button>
        </div>
        <div className="px-3 py-2">
          <input
            type="text"
            placeholder="Search…"
            className="w-full text-xs px-3 py-1.5 border border-slate-200 rounded-lg focus:outline-none focus:border-primary"
            onChange={(e) => refetchWithQuery({ page: 1, search: e.target.value })}
          />
        </div>
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex justify-center py-8"><Spin size="small" /></div>
          ) : threads.length === 0 ? (
            <div className="text-center text-slate-400 text-xs py-8">No threads yet.</div>
          ) : (
            threads.map((t) => (
              <div
                key={t.id}
                onClick={() => setActiveThread(t)}
                className={`px-4 py-3 cursor-pointer border-b border-slate-50 hover:bg-slate-50 transition-colors ${activeThread?.id === t.id ? "bg-slate-100 border-l-2 border-l-primary" : ""}`}
              >
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-sm font-medium text-slate-800 truncate">{t.subject || "No subject"}</span>
                  <Tag color={STATUS_COLOR[t.status]} className="text-[10px] ml-1 shrink-0">{t.status}</Tag>
                </div>
                {t.assigned_to_name && <div className="text-xs text-slate-400 truncate">To: {t.assigned_to_name}</div>}
                {t.last_message && <div className="text-xs text-slate-500 truncate mt-0.5">{t.last_message}</div>}
                <div className="text-[10px] text-slate-300 mt-1">{t.last_message_at ? dayjs(t.last_message_at).fromNow() : "—"}</div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* chat area */}
      <div className="flex-1 overflow-hidden">
        {activeThread ? (
          <ChatPanel thread={activeThread} onResolve={() => { setActiveThread(null); refetchWithQuery({ page: 1, limit: 30 }); }} />
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-slate-400 gap-3">
            <MessageSquare size={40} className="opacity-30" />
            <p className="text-sm">Select a thread or start a new message</p>
          </div>
        )}
      </div>

      <NewThreadModal open={newOpen} onClose={() => setNewOpen(false)} />
    </div>
  );
};

export default MessagingPage;
